import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    IChecklist2UnitWithPeriodModel,
    IChecklistV2Viewer,
    ICheckpoint2AnswerModel,
    ICheckpointGroup,
    ICheckpointRange,
    ICheckpointUnitSettingModel,
    ICheckpointV2,
} from '@fi-shared/checklist/checklist-v2';
import { createMap } from '@fi-shared/utils/create-map';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import {
    FiChecklistHelperService,
    FiChecklistModuleDiscardChangesDialogData,
} from '@fi-shared/checklist/fi-checklist-helper.service';
import { Checkpoint2StatusType } from '@fi-shared/checklist/checklist-enum';
import { AppTextService } from '@fi-shared/app-text';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { NgModel } from '@angular/forms';
import { isNullOrUndefined } from 'util';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { cloneDeep, FiDateTimeFormatOptions, isDefined, isEqual } from '@fi-shared/utils';
import { SideListOptionType } from '@fi-shared/types';
import { FiNotificationHelperService } from '@fi-shared/fi-notification/fi-notification-helper.service';
import { ChecklistViewerStatus } from '@fi-shared/checklist/fi-checklist-viewer-status';
import { BehaviorSubject, Subject } from 'rxjs';
import { FiChecklist2SyncService } from '@fi-shared/checklist/fi-checklist2-sync.service';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { FiChecklistViewerSnackbarColorClass } from '@fi-shared/checklist/fi-checklist2-viewer/disabled-viewer-warning-snackbar/disabled-viewer-warning-snackbar.component';
import { FiChecklistScheduleType } from '@fi-shared/checklist/schedule/schedule-edit/fi-checklist-schedule-type';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { takeUntil } from 'rxjs/operators';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';

@Component({
    selector: 'fi-checklist2-viewer',
    templateUrl: './fi-checklist2-viewer.component.html',
    styleUrls: ['./fi-checklist2-viewer.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-checklist2-viewer' },
    standalone: false,
})
export class FiChecklist2ViewerComponent implements OnInit, OnDestroy {
    @Input() isChecklistTimeOut: boolean;
    @Input() checklistViewer: IChecklistV2Viewer;
    @Input() businessUnitId: number;
    @Input() isPreviewMode: boolean;
    @Input() fromLinkTo: boolean;
    @Input({ required: true }) auditMode: boolean;
    @Input() checklistUnitsSchedulePeriods: IChecklist2UnitWithPeriodModel[];
    @ViewChild(FiValidatingFormDirective) viewerForm: FiValidatingFormDirective;

    checkpointAnswersMap: { [checkpointId: number]: ICheckpoint2AnswerModel };
    checkpointUnitSettingsMap: { [checkpointId: number]: ICheckpointUnitSettingModel };
    private initialCheckpointUnitSettingsMap: { [checkpointId: number]: ICheckpointUnitSettingModel };
    private initialCheckpointAnswersMap: { [checkpointId: number]: ICheckpoint2AnswerModel };
    initialAnswers: ICheckpoint2AnswerModel[] = [];
    viewerStatus$ = new BehaviorSubject<ChecklistViewerStatus>(ChecklistViewerStatus.Active);
    isViewerDisabled$ = new BehaviorSubject<boolean>(false);
    private dirtyAnswerCheckpointIds: number[] = [];

    completedCount: number;
    notCompletedCount: number;
    deviationCheckpointIds: number[] = [];
    notFullyCompletedCheckpointIds: number[] = [];
    highlightedCheckpointIds: number[] = [];

    constructor(
        private fiChecklist2Service: FiChecklist2Service,
        private fiChecklistHelperService: FiChecklistHelperService,
        private fiCheckpoint2HelperService: FiCheckpoint2HelperService,
        private fiChecklist2SyncService: FiChecklist2SyncService,
        private fiUserDataService: FiUserDataService,
        private cdr: ChangeDetectorRef,
        private appTextService: AppTextService,
        private fiStateService: FiStateService,
        private fiDialogsService: FiDialogsService,
        private saveChangesWarningService: SaveChangesWarningService,
        private snackBar: FiSnackBarService,
        private notificationHelper: FiNotificationHelperService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    ngOnInit(): void {
        if (
            !this.isPreviewMode &&
            this.fiChecklistHelperService.isAuditOnDemandNotActive(
                this.checklistViewer.IsActive,
                this.checklistViewer.ScheduleType,
            )
        ) {
            // https://chainformation.atlassian.net/browse/FI-6026
            // https://chainformation.atlassian.net/browse/FI-6114
            // https://chainformation.atlassian.net/browse/FI-5773?focusedCommentId=57657
            this.checklistViewer.Answers.forEach((checkpointAnswer) =>
                this.fiChecklistHelperService.clearCheckpoint(checkpointAnswer),
            );
        }
        this.setViewerSettings();
        this.updateCompetitionData();
        this.setViewerStatus();
        this.setInitialCheckpointsAnswersData();
        this.checkNotVisited();
        this.subscribeOnChecklistViewerAnswerChange();
        this.startSaveChangesWatch();
        this.subscribeOnUserBusinessUnitChange();
    }

    private subscribeOnUserBusinessUnitChange() {
        this.fiUserDataService.onPreferredBusinessUnitChanged$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((bu) => {
            if (!this.auditMode) {
                this.onReloadViewerState(bu.Id);
            }
        });
    }

    private setViewerSettings() {
        this.setAnswersAndUnitSettingsMap();
        this.initialAnswers = cloneDeep(this.checklistViewer.Answers);
        this.startSaveChangesWatch();
    }

    private startSaveChangesWatch() {
        // https://chainformation.atlassian.net/browse/FI-5596
        // we should track changes for each answer separately

        this.saveChangesWarningService.startForMultipleObjects(
            this.checklistViewer.Answers.map((_, idx) => () => {
                // we should not use variable from closure
                return this.checklistViewer.Answers[idx];
            }),
            {
                headerText: this.appTextService.get('checklist.leave_warning_header'),
                messageText: this.appTextService.get('checklist.leave_warning_message'),
                yesButtonText: this.appTextService.get('common.confirm'),
                isTopCloseButtonShown: true,
            },
        );
    }

    private stopSaveChangesWatch() {
        this.saveChangesWarningService.stop();
    }

    private setAnswersAndUnitSettingsMap() {
        this.checkpointAnswersMap = createMap(this.checklistViewer.Answers, 'CheckpointId');
        this.checkpointUnitSettingsMap = createMap(this.checklistViewer.CheckpointUnitSettings, 'CheckpointId');
    }

    private setViewerStatus() {
        const viewerStatus = this.fiChecklistHelperService.getViewerStatus(this.checklistViewer);
        this.viewerStatus$.next(viewerStatus);
        this.isViewerDisabled$.next(this.viewerStatus$.value !== ChecklistViewerStatus.Active);
    }

    private setInitialCheckpointsAnswersData() {
        this.initialCheckpointUnitSettingsMap = cloneDeep(this.checkpointUnitSettingsMap);
        this.initialCheckpointAnswersMap = cloneDeep(this.checkpointAnswersMap);
    }

    private getCheckpointAnswerChangedNotApplicableIds(): string[] {
        const checkpointIds: string[] = Object.keys(this.initialCheckpointAnswersMap);
        return checkpointIds.filter((checkpointId: string) => {
            const currentAnswer: ICheckpoint2AnswerModel = this.checkpointAnswersMap[checkpointId];
            return (
                isDefined(currentAnswer?.Value) &&
                this.checkpointUnitSettingsMap[checkpointId].IsNotApplicable &&
                !this.initialCheckpointUnitSettingsMap[checkpointId].IsNotApplicable
            );
        });
    }

    getChecklistDescription(): string {
        return this.checklistViewer.Checklist.Description;
    }

    onBackButtonClick() {
        window.history.back();
    }

    isChecklistDescriptionCardShown(): boolean {
        return !!this.getChecklistDescription() || !!this.checklistViewer.Checklist.MediaBankFileReferences?.length;
    }

    save() {
        const checkpointAnswerChangedNotApplicableIds: string[] = this.getCheckpointAnswerChangedNotApplicableIds();
        if (!!checkpointAnswerChangedNotApplicableIds?.length) {
            this.fiChecklistHelperService
                .confirmMarkNotApplicable(
                    this.appTextService.get('checklist.checkpoint_save_not_applicable_warning_header'),
                    this.appTextService.get('checklist.checkpoint_save_not_applicable_warning_message'),
                )
                .onYesButtonClick(() => {
                    checkpointAnswerChangedNotApplicableIds.forEach((checkpointId: string) => {
                        const initialAnswer: ICheckpoint2AnswerModel = this.initialCheckpointAnswersMap[checkpointId];
                        const answerIndex: number = this.checklistViewer.Answers.findIndex(
                            (answer: ICheckpoint2AnswerModel) => {
                                return answer.CheckpointId === initialAnswer.CheckpointId;
                            },
                        );
                        this.checklistViewer.Answers[answerIndex] = cloneDeep(initialAnswer);
                    });
                    this.saveChecklistViewer();
                });
            return;
        }
        this.saveChecklistViewer();
    }

    private saveChecklistViewer() {
        // we should save only answers, manually changed by user
        const checklistViewerForSave: IChecklistV2Viewer = cloneDeep(this.checklistViewer);
        checklistViewerForSave.Answers = checklistViewerForSave.Answers.filter((answer) =>
            this.dirtyAnswerCheckpointIds.includes(answer.CheckpointId),
        );
        this.fiChecklist2Service
            .saveChecklistViewer(checklistViewerForSave, this.auditMode, this.businessUnitId)
            .then(() => {
                /*this.fiChecklist2SyncService.updateNotCompletedChecklists(this.checklistViewer.ChecklistToScheduleId,
                this.checklistViewer.BusinessUnitId,
                this.notCompletedCount,
                false,
                this.auditMode);*/
                this.navigateToChecklistList();
            });
    }

    onAnswerChanged(answer: ICheckpoint2AnswerModel) {
        this.viewerForm.updateValidity();
        this.updateCompetitionData();
        this.cdr.markForCheck();

        if (!this.dirtyAnswerCheckpointIds.includes(answer.CheckpointId)) {
            this.dirtyAnswerCheckpointIds.push(answer.CheckpointId);
        }
        const isAnswerCompleted = !this.notFullyCompletedCheckpointIds.includes(answer.CheckpointId);
        if (isAnswerCompleted && this.highlightedCheckpointIds.includes(answer.CheckpointId)) {
            this.highlightAnswerCards(this.notFullyCompletedCheckpointIds);
        }
    }

    onApplicableChanged() {
        if (this.checklistViewer.IsSetNotApplicable) {
            // all current answers on backend were deleted permanently, so we should reset them in viewer
            this.saveChangesWarningService.stop();
            this.initialAnswers.forEach((ans) => {
                this.fiChecklist2SyncService.resetAnswer(ans);
                this.checklistViewer.Answers = cloneDeep(this.initialAnswers);
                this.saveChangesWarningService.start(() => this.checklistViewer.Answers);
            });
        }

        this.setAnswersAndUnitSettingsMap();
        this.dirtyAnswerCheckpointIds = [];
        this.updateCompetitionData();
        this.setViewerStatus();
    }

    getSaveButtonLabel(): string {
        return this.appTextService.get(this.notCompletedCount ? 'common.save' : 'done');
    }

    updateCompetitionData() {
        this.completedCount = this.notCompletedCount = 0;
        this.deviationCheckpointIds = [];
        if (this.checklistViewer.IsSetNotApplicable) {
            this.notCompletedCount = this.fiChecklistHelperService.getCheckpointsAmount(
                this.checklistViewer.Checklist.CheckpointGroups,
            );
            return;
        }

        this.notFullyCompletedCheckpointIds = [];
        this.checklistViewer.Checklist.CheckpointGroups.forEach((checkpointGroup) => {
            checkpointGroup.Checkpoints.forEach((checkpoint) => {
                const answer = this.checkpointAnswersMap[checkpoint.Id];
                const setting = this.checkpointUnitSettingsMap[checkpoint.Id];
                if (!setting.IsNotApplicable) {
                    const checkpointStatus = this.getCheckpointStatus(answer.Value, checkpoint.Ranges);

                    const isCommentMandatoryAndSkipped: boolean =
                        this.isCommentMandatory(answer.Value, checkpoint.Ranges, checkpoint.Conditions) &&
                        this.fiChecklistHelperService.isCommentEmpty(answer);

                    const isAttachmentMandatoryAndSkipped: boolean =
                        this.isAttachmentMandatory(answer.Value, checkpoint.Ranges, checkpoint.Conditions) &&
                        this.fiChecklistHelperService.isAttachmentsEmpty(answer);

                    const isEmptyAnswerWithCommentAttachments =
                        this.fiChecklistHelperService.isEmptyAnswerWithCommentAttachments(answer);

                    if (
                        isCommentMandatoryAndSkipped ||
                        isAttachmentMandatoryAndSkipped ||
                        isEmptyAnswerWithCommentAttachments
                    ) {
                        this.notCompletedCount++;
                        this.notFullyCompletedCheckpointIds.push(answer.CheckpointId);
                    } else if (checkpointStatus === Checkpoint2StatusType.NotFilledOut) {
                        this.notCompletedCount++;
                    } else {
                        this.completedCount++;
                        if (checkpointStatus === Checkpoint2StatusType.NotOk) {
                            this.deviationCheckpointIds.push(answer.CheckpointId);
                        }
                    }
                }
            });
        });
    }

    private isCommentMandatory(answerValue: number, checkpointRanges, checkpointConditions): boolean {
        return this.fiChecklistHelperService.isCommentMandatory(answerValue, checkpointRanges, checkpointConditions);
    }

    private isAttachmentMandatory(answerValue: number, checkpointRanges, checkpointConditions): boolean {
        return this.fiChecklistHelperService.isAttachmentMandatory(answerValue, checkpointRanges, checkpointConditions);
    }

    private getCheckpointStatus(answerValue: number, ranges: ICheckpointRange[]) {
        const status = this.fiCheckpoint2HelperService.getCheckpointStatus(answerValue, ranges);
        return status;
    }

    onValidationError(inputsWithErrors: NgModel[]) {
        const incompleteCheckpointNames: string[] = inputsWithErrors
            .map((inputsWithError) => {
                const model: ICheckpoint2AnswerModel = inputsWithError.model;
                const checkpointId = model?.CheckpointId;
                return checkpointId ? this.getCheckpointNameById(model.CheckpointId) : undefined;
            })
            .filter((checkpointName: string) => {
                return !!checkpointName;
            });
        const uniqueIncompleteCheckpointNames = [...new Set(incompleteCheckpointNames)];
        this.fiDialogsService
            .confirm({
                headerText: this.appTextService.get('checklist.checkpoint_incomplete'),
                isTopCloseButtonShown: true,
                messageText: this.appTextService.get('checklist.checkpoint_incomplete_dialog_message', {
                    '{0}': uniqueIncompleteCheckpointNames.join('<br>'),
                }),
                yesButtonText: this.appTextService.get('common.confirm'),
                noButtonText: this.appTextService.get('common.cancel'),
                colorClass: 'orange',
                headerIconClass: 'orange far fa-circle-exclamation',
                isNoButtonPrimaryColor: true,
                preventUppercaseButtonLabel: true,
                config: {
                    panelClass: 'fi-rounded-dialog-panel',
                },
            })
            .onYesButtonClick(() => {
                this.checklistViewer.Answers.filter((answer) =>
                    this.fiChecklistHelperService.isEmptyAnswerWithCommentAttachments(answer),
                ).forEach((answer) => {
                    this.fiChecklistHelperService.clearCheckpoint(answer);
                });
                this.save();
            })
            .onNoButtonClick(() => {
                this.highlightAnswerCards(this.notFullyCompletedCheckpointIds);
            });
    }

    getCheckpointNameById(checkpointId: number): string {
        let allCheckpoints: ICheckpointV2[] = [];
        this.checklistViewer.Checklist.CheckpointGroups.forEach((CheckpointGroups: ICheckpointGroup) => {
            allCheckpoints.push(...CheckpointGroups.Checkpoints);
        });
        return allCheckpoints.find((checkpoint: ICheckpointV2): boolean => checkpoint.Id === checkpointId).Name;
    }

    highlightAnswerCards(checkpointIds: number[]) {
        // we should use here checkpoint ids instead of answer ids (which can be 0)
        this.highlightedCheckpointIds = checkpointIds;
        this.cdr.markForCheck();
    }

    onChecklistDeleted() {
        this.navigateToChecklistList();
    }

    private navigateToChecklistList() {
        this.stopSaveChangesWatch();

        this.fiStateService.checkReturnToState(() => {
            // no saved state for returning, just go to list
            const rootState = FiStateNames.getChecklistV2RootState(this.auditMode);
            this.fiStateService.go(rootState.List, { filterType: SideListOptionType.SharedWithMe });
        });
    }

    private checkNotVisited() {
        if (this.notificationHelper.isNotVisited(this.checklistViewer.Checklist)) {
            this.fiChecklist2Service.markChecklistsAsVisited([this.checklistViewer.Checklist.Id]).then(() => {
                this.notificationHelper.setIsVisited(this.checklistViewer.Checklist);
            });
        }
    }

    private subscribeOnChecklistViewerAnswerChange() {
        this.fiChecklist2SyncService.subscribeOnChecklistAnswersChange({
            viewer: this.checklistViewer,
            ngOnDestroy$: this.ngOnDestroy$,
            afterChange: (eventData) => {
                this.setAnswersAndUnitSettingsMap();
                this.updateCompetitionData();
                this.setViewerStatus();

                const updatedCheckpointIds: number[] = eventData.UpdatedAnswers.map((item) => item.CheckpointId).concat(
                    eventData.UpdatedCheckpoint2UnitSettings.map((item) => item.CheckpointId),
                );
                updatedCheckpointIds.forEach((checkpointId) => {
                    const answerIdx = this.checklistViewer.Answers.findIndex(
                        (ans) => checkpointId === ans.CheckpointId,
                    );
                    // reset initial objects by idx
                    this.saveChangesWarningService.resetInitialObjectByIdx(answerIdx);
                });

                this.snackBar.showSnackbar({
                    textMessage: this.appTextService.get('checklist.checklist_updated_by_other_user', {
                        '{user_name}': eventData.UserFullName,
                    }),
                });

                this.cdr.markForCheck();
            },
        });
    }

    onReloadViewerState(businessUnitId?: number, isChecklistTimeOut?: boolean) {
        // for non audit checklists business unit always should be user's preferred one
        let targetBusinessUnitId = this.fiUserDataService.getUserBusinessUnitId();
        if (this.isPreviewMode) {
            // current business unit should be preserved for view mode
            // https://chainformation.atlassian.net/browse/FI-5773?focusedCommentId=55406
            targetBusinessUnitId = this.businessUnitId;
        } else if (this.auditMode) {
            targetBusinessUnitId = businessUnitId;
        }

        this.fiStateService.go(
            FiStateNames.getChecklistV2RootState(this.auditMode).View,
            { businessUnitId: targetBusinessUnitId, isChecklistTimeOut },
            { reload: true, location: 'replace' },
        );
    }

    getChecklistTimeoutSnackbarMessage(): string {
        if (this.checklistViewer.ScheduleType === FiChecklistScheduleType.Recurring)
            return this.appTextService.get('checklist.schedule_time_passed_recurring', {
                '{0}': this.fiFormatDateService.formatDate(
                    this.checklistViewer.StartDateTimeStr,
                    FiDateTimeFormatOptions,
                    { showToday: false, showUTC: true },
                ),
            });
        return this.appTextService.get('checklist.schedule_time_passed');
    }

    onInvalidMediaBankFileIdsChanged($event: number[]) {
        this.checklistViewer.InvalidMediaBankFileIds = this.checklistViewer.InvalidMediaBankFileIds.concat($event);
    }

    isSaveDisabled(): boolean {
        return this.isViewerDisabled$.value && !this.dirtyAnswerCheckpointIds.length;
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly FiDateTimeFormatOptions = FiDateTimeFormatOptions;
    protected readonly FiChecklistViewerSnackbarColorClass = FiChecklistViewerSnackbarColorClass;
}
